// import L from "leaflet";

import axios from "axios";

axios.defaults.headers.common["Authorization"] =
  "Basic " + process.env.VUE_APP_API_KEY;
var { gunzip } = require("zlib");
const { promisify } = require("util");
const do_gunzip = promisify(gunzip);

// import {
//   getNotCached,
//   getIndicesNeededForeca,
//   getIndicesNeeded,
//   getLayer,
// } from "@/utils/auxiliary";

import { getCountryCode } from "@/utils/geocoding";

const BASE_URL_local = "/";

/**
 * Requests the missing tiles if needed and updates the cache
 *
 * @param {object} this_p The pointer to the vue instance.
 * @param {object} params
 * @param {number} layer
 * @param {object} leafletMap The pointer to the map
 * @param {number} qid The id of the request in the queue
 */
export async function getDataAPI(this_p, params, layer, timeIdx) {
  const VERBOSE_OUTPUT = true;

  try {
    // Get country code
    const code = await getCountryCode(this_p.lng, this_p.lat);
    params.lon = this_p.lng;
    params.lat = this_p.lat;

    params.timestampFull = this_p.timestampFull;
    params.timestampOriginal = this_p.timestampOriginal;

    if (VERBOSE_OUTPUT) console.log("params:", params);

    const url = `${BASE_URL_local}api/tiles`;

    // Determine responseType based on dataSource
    const isCompressed =
      this_p.dataSource !== "foreca" && this_p.dataSource !== "foreca_global";
    const responseType = isCompressed ? "arraybuffer" : "";

    // Request tiles.
    // Two-backend sync contract: a backend that holds an OLDER forecast run
    // than this client answers 404 ("backend data is stale") instead of
    // serving old tiles. Behind the round-robin balancer a retry lands on
    // the other backend, so retry a couple of times on 404 only; all other
    // errors keep today's behavior (handled by the outer catch).
    let res;
    let staleRetriesLeft = 2;
    for (;;) {
      try {
        res = await axios.get(url, {
          params,
          responseType,
        });
        break;
      } catch (err) {
        const status = err.response && err.response.status;
        if (status === 404 && staleRetriesLeft > 0) {
          staleRetriesLeft--;
          console.warn(
            ":: getDataAPI: backend reported stale data (404), retrying; retries left:",
            staleRetriesLeft
          );
          await new Promise((resolve) => setTimeout(resolve, 700));
          continue;
        }
        throw err;
      }
    }

    if (res.status !== 200) {
      this_p.debugPanel4Out("No data: " + res.status);
      return;
    }

    this_p.debugPanel4Out(res.status);

    if (VERBOSE_OUTPUT) {
      console.log(":: getDataAPI: Response received", res);
      console.log(":: getDataAPI: Will be put to layer", layer);
    }

    // Get data from response
    let data;
    if (isCompressed) {
      // Handle compressed response
      const buffer = Buffer.from(res.data, "hex");

      if (VERBOSE_OUTPUT) console.log(":: Buffer:", buffer);

      try {
        const decompressedData = await do_gunzip(buffer);
        const payload = JSON.parse(decompressedData);
        // Two-backend sync contract: learn/advance the run floor and honor
        // the backend's clear request before the tiles are processed.
        applySyncMetadata(this_p, payload);
        data = payload.tiles;

        if (VERBOSE_OUTPUT) console.log(":: Decompressed data:", data);
      } catch (err) {
        handleError(this_p, res.status, err);
        return;
      }
    } else {
      // Handle uncompressed response
      data = res.data;

      if (VERBOSE_OUTPUT) console.log(":: Parsed data:", data);

      // Extract tiles array from the response object
      if (data && Array.isArray(data.tiles)) {
        // Identity held BEFORE this response is processed — needed to decide
        // whether a clearFrontendCache flag reflects a real run change or
        // only a stale in-flight echo (see below).
        const prevIdentity = this_p.latestDataTimestamp[this_p.dataSource];

        // set forecast time if provided
        if (data.forecastTime) {
          this_p.latestForecastTime = data.forecastTime;
          let newTS;
          if (this_p.dataSource === "foreca" || this_p.dataSource === "foreca_global") {
            newTS = data.forecastTime;
          } else {
            newTS = Date.parse(data.forecastTime);
          }
          let testTime = new Date("2024-11-02T16:15:00Z");

          console.log("*****p arsed testTime is ", testTime);
          console.log("***** parsed time is ", newTS);

          this_p.latestDataTimestamp[this_p.dataSource] = newTS;
          console.log(
            "***** this_p.latestDataTimestamp[this_p.dataSource] time is ",
            this_p.latestDataTimestamp[this_p.dataSource]
          );
        } else {
          console.warn("forecastTime missing in response for datasource:", this_p.dataSource);
        }
        // Identity-driven invalidation for the foreca sources (F4): the
        // backend sets clearFrontendCache when this client's echoed forecast
        // identity does not match the serving region's current one. Foreca is
        // excluded from time-based eviction (all regions share one store), so
        // honoring this flag is what purges an outdated run.
        //
        // Race guard: on a fresh start the slider fires several requests
        // before the first response adopts the identity — they all echo the
        // empty/old identity, so EVERY response of that burst carries the
        // flag, and honoring each one would wipe the frames its concurrent
        // siblings just cached (reproduced: 1 of 5 frames survived; felt as
        // "cache never works" right after app start). The flag reflects the
        // ECHO at request time; the authoritative question at response time
        // is whether the response's identity differs from the one we hold
        // NOW. Same identity -> we are already current -> never wipe.
        if (
          data.clearFrontendCache &&
          data.forecastTime &&
          data.forecastTime !== prevIdentity
        ) {
          console.log(
            "** clearFrontendCache: run changed for",
            this_p.dataSource,
            "- dropping its cached tiles **"
          );
          clearSourceCache(this_p, this_p.dataSource);
        }
        data = data.tiles;
      } else {
        console.error("Invalid data format received from the server.");
        return;
      }
    }

    // Process the data
    processData(
      this_p,
      data,
      layer,
      timeIdx,
      code,
      isCompressed,
      VERBOSE_OUTPUT
    );
  } catch (error) {
    console.error("Error in getDataAPI:", error);
    this_p.debugPanel4Out("Error: " + error.message);
  }
}
function handleError(this_p, status, err) {
  this_p.debugResponses("ERRORzip" + status);
  this_p.debugPanel4Out("ERROR while unzipping data: " + err);
  console.error("ERROR:: getDataAPI: An error occurred in gunzip:", err);
  process.exitCode = 1;
}

/**
 * applySyncMetadata handles the two-backend consistency fields of a tiles
 * response (compressed sources: nw, hr_*, opera):
 * - keeps latestDataTimestamp as a MONOTONIC floor — the newest run this
 *   client has ever seen. It never regresses, so the forecastUpdate echo we
 *   send always names that newest run even when two backends briefly hold
 *   different runs;
 * - clears the affected source's cache when the backend flags that this
 *   client still holds an older run (clearFrontendCache).
 * Foreca sources keep their own identity-string flow untouched.
 */
function applySyncMetadata(this_p, payload) {
  if (!payload) return;
  const source = this_p.dataSource;
  if (source === "foreca" || source === "foreca_global") return;

  // Floor held BEFORE this response — see the race guard below.
  const prevFloor = this_p.latestDataTimestamp[source];

  let newTS = NaN;
  if (payload.forecastTime) {
    newTS = Date.parse(payload.forecastTime);
    if (
      !isNaN(newTS) &&
      (typeof prevFloor !== "number" || isNaN(prevFloor) || newTS > prevFloor)
    ) {
      this_p.latestDataTimestamp[source] = newTS;
      this_p.latestForecastTime = payload.forecastTime;
    }
  }

  // Race guard (same as the foreca path): on a fresh start several requests
  // fire before the first response adopts the run floor, so they all echo
  // the empty/old value and EVERY response of that burst carries
  // clearFrontendCache — honoring each one would wipe the frames its
  // concurrent siblings just cached. The flag reflects the echo at request
  // time; the authoritative question at response time is whether the
  // response's run differs from the floor we hold NOW. Same run -> we are
  // already current -> never wipe.
  if (payload.clearFrontendCache && !isNaN(newTS) && newTS !== prevFloor) {
    console.log(
      "** clearFrontendCache: run changed for",
      source,
      "- dropping its cached tiles **"
    );
    clearSourceCache(this_p, source);
  }
}

// clearSourceCache empties the tile cache of a single datasource in place
// (Map.clear keeps the Map instances, which the rest of the app holds
// references to — same technique as App.vue clearCache).
function clearSourceCache(this_p, source) {
  const storeMap = {
    nw: this_p.tiles,
    hr_de: this_p.tilesHRDE,
    hr_fr: this_p.tilesHRFR,
    hr_nl: this_p.tilesHRNL,
    hr_bi: this_p.tilesHRUK,
    hr_be: this_p.tilesHRBE,
    hr_sc: this_p.tilesHRSC,
    hr_ip: this_p.tilesHRES,
    opera: this_p.tilesOpera,
    foreca: this_p.tilesForeca,
    foreca_global: this_p.tilesForecaGlobal,
  };
  const store = storeMap[source];
  if (!store) return;
  for (let t = 0; t < store.length; t++) {
    if (!store[t]) continue;
    for (let z = 0; z < store[t].length; z++) {
      if (store[t][z] && typeof store[t][z].clear === "function") {
        store[t][z].clear();
      }
    }
  }
}

function processData(
  this_p,
  data,
  layer,
  timeIdx,
  code,
  isCompressed,
  VERBOSE_OUTPUT
) {
  if (!Array.isArray(data) || data.length === 0) {
    console.warn("No data available to process.");
    return;
  }

  // Set data source and availability based on the source of the data
  setDataSource(this_p, data[0].Source, code);

  if (VERBOSE_OUTPUT) console.log("Current dataSource:", this_p.dataSource);

  // Map dataSource to tiles and tileType
  const tilesAndType = getTilesAndType(this_p, timeIdx, layer, isCompressed);

  if (!tilesAndType) {
    console.error("Unknown dataSource:", this_p.dataSource);
    return;
  }

  const { array: tilesArray, map: tileMap, tileType } = tilesAndType;

  console.log("this_p.dataSource:", this_p.dataSource);

  // Add data to cache
  for (const item of data) {
    this_p.addToCache(
      tileMap,
      item,
      layer,
      timeIdx,
      tileType,
      this_p.dataSource
    );
  }

  // Update the tiles in the Vue instance
  this_p.$set(tilesArray[timeIdx], layer, tileMap);

  if (VERBOSE_OUTPUT) {
    console.log("Updated tiles:", tilesArray);
    console.log("Time Index:", timeIdx);
    console.log("Layer:", layer);
    console.log("Tile Map:", tileMap);
  }
}
function setDataSource(this_p, source, code) {
  switch (source) {
    case "nw":
      this_p.nw_available = true;
      this_p.setDataSourceFromCountryCode("nl");
      break;
    case "hr_de":
      this_p.setDataSourceFromCountryCode("de");
      break;
    case "hr_fr":
      this_p.setDataSourceFromCountryCode("fr");
      break;
    case "hr_nl":
      this_p.setDataSourceFromCountryCode("nl");
      break;
    case "hr_bi":
      this_p.setDataSourceFromCountryCode("bi");
      break;
    case "hr_be":
      this_p.setDataSourceFromCountryCode("be");
      break;
    case "hr_sc":
      this_p.setDataSourceFromCountryCode("sc");
      break;
    case "hr_ip":
      this_p.setDataSourceFromCountryCode("ip");
      break;
    case "opera":
      this_p.setDataSourceFromCountryCode("fr");
      break;
    case "opera_replace_nw":
      this_p.setDataSourceFromCountryCode("fr");
      this_p.nw_available = false;
      break;
    case "foreca":
    case "foreca_global":
      this_p.setDataSourceFromCountryCode(code);
      break;
    default:
      console.warn("Unknown source:", source);
      break;
  }
}

function getTilesAndType(this_p, timeIdx, layer, isCompressed) {
  const dataSourceMap = {
    nw: {
      array: this_p.tiles,
      map: this_p.tiles[timeIdx][layer],
      tileType: "custom",
    },
    hr_de: {
      array: this_p.tilesHRDE,
      map: this_p.tilesHRDE[timeIdx][layer],
      tileType: "custom",
    },
    hr_fr: {
      array: this_p.tilesHRFR,
      map: this_p.tilesHRFR[timeIdx][layer],
      tileType: "custom",
    },
    hr_nl: {
      array: this_p.tilesHRNL,
      map: this_p.tilesHRNL[timeIdx][layer],
      tileType: "custom",
    },
    hr_bi: {
      array: this_p.tilesHRUK,
      map: this_p.tilesHRUK[timeIdx][layer],
      tileType: "custom",
    },
    hr_be: {
      array: this_p.tilesHRBE,
      map: this_p.tilesHRBE[timeIdx][layer],
      tileType: "custom",
    },
    hr_sc: {
      array: this_p.tilesHRSC,
      map: this_p.tilesHRSC[timeIdx][layer],
      tileType: "custom",
    },
    hr_ip: {
      array: this_p.tilesHRES,
      map: this_p.tilesHRES[timeIdx][layer],
      tileType: "custom",
    },
    opera: {
      array: this_p.tilesOpera,
      map: this_p.tilesOpera[timeIdx][layer],
      tileType: "custom",
    },
    foreca: {
      array: this_p.tilesForeca,
      map: this_p.tilesForeca[timeIdx][layer],
      tileType: isCompressed ? "custom" : "native",
    },
    foreca_global: {
      array: this_p.tilesForecaGlobal,
      map: this_p.tilesForecaGlobal[timeIdx][layer],
      tileType: isCompressed ? "custom" : "native",
    },
  };

  const dataSourceKey = this_p.dataSource.trim().toLowerCase();

  console.log("-fffffffffff");
  console.log("dataSourceMap:", dataSourceMap);
  console.log("this_p.dataSource:", this_p.dataSource);
  console.log("dataSourceKey:", dataSourceKey);
  console.log("Selected dataSource:", dataSourceMap[dataSourceKey]);

  return dataSourceMap[dataSourceKey] || null;
}
